// Input phase helpers (§18.1 step 3, §2.3, §2.5, §2.6): press decoding from wrapping counters,
// escape-input counting, the radial move deadzone and the assisted attack direction.
import { TUNE } from '../constants';
import { BS } from '../ids';
import { DEG, setV } from '../math';
import type { Bean, InputFrame, Vec2 } from '../types';
import { dangleBetween, dcos, dirOf, dsin } from './dmath';

export interface PressSet {
  shove: boolean; grab: boolean; jump: boolean; item: boolean; honk: boolean;
  /** Emote id 1–4 pressed this tick, else 0. */
  emote: number;
  /** Escape inputs this tick (only counted while GRABBED): every press plus move-axis sign flips. */
  escape: number;
}

export function createPressSet(): PressSet {
  return { shove: false, grab: false, jump: false, item: false, honk: false, emote: 0, escape: 0 };
}

/** Largest honored angle between the assisted attack vector and facing (§2.5). */
const ASSIST_MAX = TUNE.shove.assistMaxDeg * DEG;

const MOD4 = 16;
const MOD3 = 8;
const MOD2 = 4;

function pressCount(now: number, prev: number, mod: number): number {
  return (((now - prev) % mod) + mod) % mod;
}

function axisSign(v: number): number {
  const t = TUNE.grab.escapeAxisThreshold;
  return v > t ? 1 : v < -t ? -1 : 0;
}

/**
 * Presses since `bean.prev` from the frame's counters, then updates `bean.prev`. Several presses
 * in one tick collapse to one, except that while GRABBED every press (and every sign flip of a
 * move axis beyond ±0.5) counts toward `escape`. A null frame is neutral with unchanged counters.
 */
export function decodePresses(bean: Bean, frame: InputFrame | null, out: PressSet = createPressSet()): PressSet {
  out.shove = out.grab = out.jump = out.item = out.honk = false;
  out.emote = 0;
  out.escape = 0;
  if (!frame) return out;

  const p = bean.prev;
  const dShove = pressCount(frame.cShove & 15, p.shove, MOD4);
  const dGrab = pressCount(frame.cGrab & 15, p.grab, MOD4);
  const dJump = pressCount(frame.cJump & 15, p.jump, MOD4);
  const dItem = pressCount(frame.cItem & 15, p.item, MOD4);
  const dHonk = pressCount(frame.cHonk & 7, p.honk, MOD3);
  const dEmote = pressCount(frame.cEmote & 3, p.emote, MOD2);

  out.shove = dShove > 0;
  out.grab = dGrab > 0;
  out.jump = dJump > 0;
  out.item = dItem > 0;
  out.honk = dHonk > 0;
  out.emote = dEmote > 0 && frame.emote >= 1 && frame.emote <= 4 ? frame.emote : 0;

  let flips = 0;
  const sx = axisSign(frame.mx);
  if (sx !== 0) {
    if (p.mxSign !== 0 && sx !== p.mxSign) flips++;
    p.mxSign = sx;
  }
  const sy = axisSign(frame.my);
  if (sy !== 0) {
    if (p.mySign !== 0 && sy !== p.mySign) flips++;
    p.mySign = sy;
  }
  if (bean.state === BS.GRABBED) out.escape = dShove + dGrab + dJump + dItem + dHonk + dEmote + flips;

  p.shove = frame.cShove & 15;
  p.grab = frame.cGrab & 15;
  p.jump = frame.cJump & 15;
  p.item = frame.cItem & 15;
  p.honk = frame.cHonk & 7;
  p.emote = frame.cEmote & 3;
  return out;
}

/** Radial deadzone with rescale (§2.3): |m| < 0.15 → 0, otherwise remapped so the edge maps to 0. */
export function applyDeadzone(mx: number, my: number, out: Vec2): Vec2 {
  const m = Math.hypot(mx, my);
  const dz = TUNE.bean.inputDeadzone;
  if (m < dz) return setV(out, 0, 0);
  const k = (Math.min(m, 1) - dz) / (1 - dz) / m;
  return setV(out, mx * k, my * k);
}

/**
 * Attack direction (§2.5): the normalized assisted vector (sx, sy) when non-zero and within
 * `assistMaxDeg` of facing, otherwise the facing direction.
 */
export function assistedDir(facing: number, sx: number, sy: number, out: Vec2): Vec2 {
  const l = Math.hypot(sx, sy);
  if (l > 0) {
    const ux = sx / l;
    const uy = sy / l;
    if (dangleBetween(dcos(facing), dsin(facing), ux, uy) <= ASSIST_MAX) return setV(out, ux, uy);
  }
  return dirOf(out, facing);
}
